import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight, BookOpen, Newspaper, Sparkles } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import Reveal from "@/components/Reveal";
import { api } from "@/lib/api";
import { blogMediaUrl } from "@/lib/blogMedia";

const TOPICS = ["Market updates", "Trading guides", "Compliance", "Product news"];

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function BlogCardSkeleton() {
  return (
    <div className="flex flex-col h-full animate-pulse bg-card rounded-2xl border border-border overflow-hidden">
      <div className="aspect-[16/10] shrink-0 bg-surface-2" />
      <div className="p-5 flex flex-col flex-1">
        <div className="h-3 w-28 rounded bg-surface-2" />
        <div className="mt-2 min-h-[3.25rem] space-y-2">
          <div className="h-5 w-full rounded bg-surface-2" />
          <div className="h-5 w-4/5 rounded bg-surface-2" />
        </div>
        <div className="mt-2 min-h-[4.125rem] space-y-2 flex-1">
          <div className="h-4 w-full rounded bg-surface-2" />
          <div className="h-4 w-full rounded bg-surface-2" />
          <div className="h-4 w-2/3 rounded bg-surface-2" />
        </div>
        <div className="mt-4 h-4 w-24 rounded bg-surface-2" />
      </div>
    </div>
  );
}

const ARTICLES_GRID_CLASS =
  "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch";

function BlogPostCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="group flex flex-col h-full w-full bg-card rounded-2xl border border-border overflow-hidden shadow-[0_12px_40px_-20px_rgba(11,20,38,0.12)] hover:shadow-[0_20px_48px_-16px_rgba(40, 202, 113,0.18)] hover:border-primary/30 transition-all"
    >
      <div className="aspect-[16/10] shrink-0 bg-surface-2 overflow-hidden">
        {post.thumbnail_url ? (
          <img
            src={blogMediaUrl(post.thumbnail_url)}
            alt=""
            crossOrigin="anonymous"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-primary/40 text-[13px] font-semibold">
            PaisaCX
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 text-[12px] text-muted-foreground shrink-0">
          <Calendar size={14} />
          {formatDate(post.published_at || post.created_at)}
        </div>
        <h2 className="mt-2 min-h-[3.25rem] text-[18px] font-semibold text-foreground line-clamp-2 group-hover:text-primary transition-colors">
          {post.title}
        </h2>
        <p className="mt-2 min-h-[4.125rem] flex-1 text-[14px] text-muted-foreground line-clamp-3 leading-relaxed">
          {post.excerpt || "\u00A0"}
        </p>
        <span className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-primary shrink-0">
          Read more <ArrowRight size={14} />
        </span>
      </div>
    </Link>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get("/api/cms/blog/posts", { params: { limit: 48 } })
      .then(({ data }) => setPosts(data.posts || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const latestDate = useMemo(() => {
    if (!posts.length) return null;
    const iso = posts[0]?.published_at || posts[0]?.created_at;
    return iso ? formatDate(iso) : null;
  }, [posts]);

  return (
    <div className="min-h-screen bg-card text-foreground" data-testid="blog-page">
      <Header />

      <section className="relative overflow-hidden cm-landing-hero-bg cm-landing-mesh border-b border-border">
        <div className="absolute inset-0 cm-landing-grid-bg opacity-40 pointer-events-none" />
        <div
          className="absolute -top-24 right-0 w-[min(520px,55vw)] h-[520px] rounded-full opacity-50 blur-3xl pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(40, 202, 113,0.14) 0%, transparent 70%)",
          }}
        />

        <div className="relative cm-container pt-28 pb-14 lg:pb-16">
          <div className="grid lg:grid-cols-[1fr_300px] gap-10 lg:gap-16 items-end">
            <Reveal variant="fade-up">
              <span className="cm-landing-eyebrow">
                <Sparkles size={13} strokeWidth={2.2} aria-hidden />
                Insights &amp; updates
              </span>
              <h1
                className="mt-5 cm-landing-section-title max-w-[720px]"
                style={{ fontSize: "clamp(34px, 4.5vw, 52px)" }}
              >
                PaisaCX Blog
              </h1>
              <p className="mt-5 max-w-[580px] text-muted-foreground text-[16px] lg:text-[17px] leading-[1.75]">
                News, guides, and product updates from the PaisaCX team — covering
                markets, compliance, and how to trade smarter on our platform.
              </p>
              <div className="mt-8 flex flex-wrap gap-2.5">
                {TOPICS.map((topic) => (
                  <span
                    key={topic}
                    className="inline-flex items-center px-3.5 py-1.5 rounded-full text-[12px] font-semibold text-foreground bg-card border border-border shadow-sm"
                  >
                    {topic}
                  </span>
                ))}
              </div>
            </Reveal>

            <Reveal variant="fade-up" delay={80} className="hidden lg:block">
              <div className="rounded-2xl border border-border bg-card/90 backdrop-blur-sm p-6 shadow-[0_24px_56px_-32px_rgba(40, 202, 113,0.18)]">
                <div className="w-12 h-12 rounded-xl bg-cm-lavender grid place-items-center mb-4">
                  <Newspaper size={22} className="text-primary" strokeWidth={2} />
                </div>
                <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-primary">
                  From the team
                </p>
                <p className="mt-2 text-[14px] text-muted-foreground leading-[1.65]">
                  Practical articles for Indian crypto traders — spot, P2P, OTC, and
                  platform announcements in one place.
                </p>
                {!loading && posts.length > 0 ? (
                  <div className="mt-5 pt-5 border-t border-border flex items-center justify-between gap-3 text-[13px]">
                    <span className="text-muted-foreground">
                      <span className="font-semibold text-foreground tabular-nums">{posts.length}</span>{" "}
                      {posts.length === 1 ? "article" : "articles"}
                    </span>
                    {latestDate ? (
                      <span className="text-muted-foreground text-right">
                        Latest · <span className="font-semibold text-foreground">{latestDate}</span>
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-card pb-20 lg:pb-24">
        <div className="cm-container">
          <div className="pt-12 lg:pt-14 border-t border-border">
            <Reveal variant="fade-up" className="mb-10 lg:mb-12">
              <p className="cm-landing-section-label mb-3">Latest articles</p>
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-[22px] lg:text-[26px] font-semibold text-foreground tracking-tight">
                  {loading ? "Loading stories…" : posts.length ? "Fresh from PaisaCX" : "Coming soon"}
                </h2>
                {!loading && posts.length > 0 ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-[12px] font-semibold text-foreground bg-surface-2 border border-border">
                    {posts.length} published {posts.length === 1 ? "post" : "posts"}
                  </span>
                ) : null}
              </div>
            </Reveal>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
              {Array.from({ length: 6 }).map((_, i) => (
                <BlogCardSkeleton key={i} />
              ))}
            </div>
          ) : posts.length === 0 ? (
            <Reveal variant="fade-up">
              <div
                className="max-w-[520px] mx-auto text-center py-16 px-8 rounded-2xl border border-dashed border-border bg-cm-surface/50"
                data-testid="blog-empty"
              >
                <div className="mx-auto w-14 h-14 rounded-2xl bg-cm-lavender grid place-items-center mb-5">
                  <BookOpen size={26} className="text-primary" strokeWidth={2} />
                </div>
                <h3 className="text-[20px] font-semibold text-foreground">No articles yet</h3>
                <p className="mt-2 text-[14px] text-muted-foreground leading-relaxed">
                  We&apos;re preparing guides and announcements. Check back soon or explore
                  the exchange in the meantime.
                </p>
                <Link
                  to="/"
                  className="mt-6 inline-flex items-center justify-center h-[44px] px-6 rounded-full brand-gradient text-primary-foreground text-[14px] font-semibold hover:brightness-110 transition-colors"
                >
                  Back to homepage
                </Link>
              </div>
            </Reveal>
          ) : (
            <div className={ARTICLES_GRID_CLASS}>
              {posts.map((post, i) => (
                <Reveal key={post.id} variant="fade-up" delay={i * 60} className="w-full h-full">
                  <BlogPostCard post={post} />
                </Reveal>
              ))}
            </div>
          )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
